import type { Standing } from '@guess/engine';
import type { GameState } from '../app/state';
import { playerName, seatIndex } from '../app/state';
import { Avatar } from './Avatar';
import { Money } from './Money';
import { Seat } from './Seat';
import { cx } from './cx';
import s from './ui.module.scss';

/**
 * Le classement.
 *
 * Le rang se recalcule a chaque cagnotte qui change — c'est le jeu. Mais la
 * LIGNE d'un joueur, elle, ne bouge plus : elle reste a la place de son
 * siege, fixe pour toute la partie. Reordonner les lignes a chaque mise a
 * jour rendait le classement illisible en pleine partie (« ca bouge trop »).
 *
 * Le podium final fait exception, et c'est le seul endroit ou trier a du
 * sens : la partie est finie, plus rien ne bougera plus jamais, donc trier
 * VRAIMENT par cagnotte devient un effet de reveal plutot qu'une gene.
 */
export interface StandingsProps {
  state: GameState;
  standings: readonly Standing[];
  title?: string;
  podium?: boolean;
  /** Qui vient d'etre touche par un pouvoir, pour un bref halo sur sa ligne. */
  flashPlayerId?: string | null;
  flashKind?: 'bonus' | 'malus' | undefined;
}

export function Standings({
  state,
  standings,
  title,
  podium = false,
  flashPlayerId = null,
  flashKind,
}: StandingsProps) {
  const byId = new Map(standings.map((standing) => [standing.playerId, standing]));
  const ranked = [...standings].sort((a, b) => b.bankroll - a.bankroll);
  const rankOf = new Map(ranked.map((standing, index) => [standing.playerId, index]));
  const leader = ranked[0]?.bankroll ?? 0;

  const order = podium
    ? ranked
    : state.players
        .map((player) => byId.get(player.id))
        .filter((standing): standing is Standing => standing !== undefined);

  return (
    <section className={cx(s.standings, podium && s.standingsPodium)}>
      {title ? <h2 className={s.standingsTitle}>{title}</h2> : null}

      <ol className={s.standingsList}>
        {order.map((standing, index) => {
          const rank = rankOf.get(standing.playerId) ?? 0;
          const gap = leader - standing.bankroll;
          const player = state.players.find((p) => p.id === standing.playerId);
          return (
            <li
              key={standing.playerId}
              className={cx(
                s.standingsRow,
                rank === 0 && s.standingsLeader,
                standing.playerId === flashPlayerId &&
                  (flashKind === 'malus' ? s.standingsFlashBad : s.standingsFlashGood),
              )}
              style={{ animationDelay: `calc(var(--dur-fast) * ${index})` }}
            >
              <span className={s.standingsRank} aria-hidden="true">
                {rank + 1}
              </span>

              <Avatar
                avatarId={player?.avatarId}
                colorId={player?.colorId}
                seatIndex={seatIndex(state, standing.playerId)}
                size="sm"
              />

              <Seat
                className={s.standingsSeat}
                name={playerName(state, standing.playerId)}
                seatIndex={seatIndex(state, standing.playerId)}
                colorId={player?.colorId}
                bot={player?.bot ?? false}
                you={standing.playerId === state.you}
                offline={player ? !player.connected : false}
              />

              <span className={s.standingsScore}>
                <Money value={standing.bankroll} sound={standing.playerId === state.you} />
                {gap > 0 ? <span className={s.standingsGap}>-{Math.round(gap)}</span> : null}
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
